import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, catchError, take, of } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ApiService, User } from '../../services/api.service';
import { parseAdminPreviewUserIdFromRouterUrl } from '../utils/admin-preview-url';

/** state.url peut omettre le query avec HashLocationStrategy ; le fragment ou location.search le portent. */
function rawUrlForAdminPreviewScan(router: Router, stateUrl: string): string {
  if (parseAdminPreviewUserIdFromRouterUrl(router, stateUrl)) {
    return stateUrl;
  }
  const routerUrl = router.url ?? '';
  if (parseAdminPreviewUserIdFromRouterUrl(router, routerUrl)) {
    return routerUrl;
  }
  const routerPath = (routerUrl.split('?')[0] || '').trim();
  if (routerPath.startsWith('/dashboard') && !routerUrl.includes('adminPreviewUserId=')) {
    // Le routeur a déjà retiré le param (ex. après createUrlTree) : ne pas relire location.hash,
    // souvent en retard sur le snapshot — sinon boucle infinie UrlTree + GET /me + site-access.
    return routerUrl || stateUrl;
  }
  if (typeof window === 'undefined' || !window.location) {
    return stateUrl;
  }
  const hash = window.location.hash ?? '';
  if (hash.length > 1) {
    const inner = hash.startsWith('#') ? hash.slice(1) : hash;
    if (inner.includes('adminPreviewUserId=')) {
      return inner.startsWith('/') ? inner : `/${inner}`;
    }
  }
  const search = window.location.search ?? '';
  if (search.includes('adminPreviewUserId=')) {
    const pathname = window.location.pathname || '/';
    return `${pathname}${search}`;
  }
  return stateUrl;
}

function userHasAdminRole(user: User | null | undefined): boolean {
  return (user?.roles ?? []).some((r) => typeof r === 'string' && r.trim().toUpperCase() === 'ADMIN');
}

/**
 * Garde d'accès : seules les routes protégées par ce garde sont accessibles aux utilisateurs connectés.
 * Si non connecté, l'utilisateur est redirigé vers la page d'accueil.
 * En cas de doute (user pas encore chargé), un appel à /me est effectué pour vérifier le cookie.
 *
 * Prévisualisation admin : si l'URL contient `adminPreviewUserId` sous `/dashboard`, on valide la session
 * sans en-têtes de délégation puis on pose le contexte admin **avant** les gardes enfants — sinon une
 * délégation support ou classique déjà en session faisait refuser `/dashboard` et rediriger ailleurs
 * (ou casser le flux) avant que le layout n'ait appliqué le paramètre.
 */
export const authGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);
  const api = inject(ApiService);
  const router = inject(Router);

  const rawUrl = rawUrlForAdminPreviewScan(router, state.url ?? '');
  const path = rawUrl.split('?')[0];
  const adminPreviewUserId = path.startsWith('/dashboard') ? parseAdminPreviewUserIdFromRouterUrl(router, rawUrl) : null;

  if (adminPreviewUserId) {
    const existing = auth.activeDelegation;
    if (
      auth.isLoggedIn
      && existing?.supportMode === 'admin_preview'
      && existing.ownerUserId === adminPreviewUserId
    ) {
      return true;
    }
    return api.getCurrentUser(undefined, { skipDelegation: true }).pipe(
      take(1),
      map((user) => {
        auth.setUser(user);
        if (userHasAdminRole(user)) {
          auth.setActiveDelegation({
            ownerUserId: adminPreviewUserId,
            ownerUsername: null,
            permissions: [],
            supportMode: 'admin_preview'
          });
        }
        return router.createUrlTree(['/dashboard'], {
          queryParams: { adminPreviewUserId: null },
          queryParamsHandling: 'merge'
        });
      }),
      catchError(() => {
        auth.setUser(null);
        return of(router.createUrlTree(['/']));
      })
    );
  }

  if (auth.isLoggedIn) {
    return true;
  }

  return api.getCurrentUser(undefined, { skipDelegation: true }).pipe(
    take(1),
    map((user) => {
      auth.setUser(user);
      return true;
    }),
    catchError(() => {
      auth.setUser(null);
      return of(router.createUrlTree(['/']));
    })
  );
};
